import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { CheckCircle2, ArrowRight, MessageCircle, FileText, Search, Scale, Gavel, Handshake, Building2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import { BRAND, SERVICES, INDUSTRIES } from "@/lib/brand";

const ICON_MAP = { FileText, Search, Scale, Gavel, Handshake, Building2 };

export default function Services() {
  useEffect(() => { document.title = `Services — ${BRAND.name}`; }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-16 md:py-20">
        <div className="max-w-3xl">
          <div className="eyebrow">Services</div>
          <h1 className="font-heading font-bold text-4xl md:text-6xl mt-4 leading-[1.05]">
            Everything you need to <span className="text-brand-gradient">recover your claim.</span>
          </h1>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed">
            Six service lines that span the entire claim journey — from documentation review to legal coordination
            and corporate claim desks.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s) => {
            const Icon = ICON_MAP[s.icon] || FileText;
            return (
              <div key={s.slug} id={s.slug} data-testid={`services-page-card-${s.slug}`} className="brand-card p-8">
                <div className="flex items-start justify-between gap-4">
                  <div className="w-12 h-12 rounded-xl bg-navy/5 text-navy flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-2xl mt-5">{s.title}</h3>
                <p className="text-slate-600 mt-2 leading-relaxed">{s.short}</p>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="text-sm text-slate-700 flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-brand mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                {s.disclaimer && (
                  <div className="mt-5 text-xs text-slate-500 italic border-l-2 border-gold pl-3">
                    {s.disclaimer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Industries strip */}
      <section className="bg-white border-y border-slate-200/70">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-16">
          <div className="eyebrow">Across every insurance line</div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl mt-3">Industries we serve</h2>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {INDUSTRIES.map((ind) => (
              <div key={ind.name} className="brand-card p-5">
                <div className="font-heading font-semibold">{ind.name}</div>
                <div className="text-xs text-slate-500 mt-1.5 leading-snug">{ind.blurb}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-16">
        <div className="rounded-[28px] bg-navy text-white p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h3 className="font-heading font-bold text-2xl md:text-3xl">Not sure which service fits your case?</h3>
            <p className="mt-2 text-slate-300">Send us your case details — we'll match you with the right specialist.</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link to="/contact" className="btn-gold text-sm" data-testid="services-cta-contact">
              Book a free review <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" className="btn-emerald text-sm" data-testid="services-cta-whatsapp">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
      <WhatsAppFAB />
    </div>
  );
}
